import React from 'react'

const MANDAYS_PER_MANMONTH = 21

interface MandaysValues {
  manDays: number
  manMonths: number
  usingManDays: boolean
}

/**
 * Creates an onChange handler for the usingManDays radio button that automatically converts
 * between mandays and manmonths when the user switches between the two.
 *
 * @param values - Current values object containing manDays, manMonths, and usingManDays
 * @param setValues - Setter function to update the values
 * @returns onChange handler function for the radio button
 */
export const createMandaysOnChangeHandler = <T extends MandaysValues>(values: T, setValues: (values: T) => void) => {
  return (v: React.ChangeEvent<HTMLInputElement>) => {
    const newUsingManDays = v.target.value === '1'
    const updatedValues = { ...values, usingManDays: newUsingManDays }

    if (newUsingManDays !== values.usingManDays) {
      if (newUsingManDays && values.manMonths > 0) {
        // Switching to mandays: convert manmonths to mandays (multiply by 21)
        updatedValues.manDays = Math.round(values.manMonths * MANDAYS_PER_MANMONTH)
        updatedValues.manMonths = 0
      } else if (!newUsingManDays && values.manDays > 0) {
        // Switching to manmonths: convert mandays to manmonths (divide by 21)
        updatedValues.manMonths = Math.round(values.manDays / MANDAYS_PER_MANMONTH)
        updatedValues.manDays = 0
      }
    }

    setValues(updatedValues as T)
  }
}

/**
 * Formats a date value to Finnish locale format (dd.mm.yyyy).
 *
 * @param value - The date value to format (can be string, Date, null, or undefined)
 * @returns Formatted date string in Finnish format, or empty string if value is null/undefined
 */
export const formatDateFi = (value?: string | Date | null): string => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString('fi-FI') // e.g. 31.12.2025
}
